/**
 * Signing in to the website, and the two places it talks to:
 *  - the Torn API, straight from this browser with your own key (or through the extension,
 *    which keeps the key — then this site never sees it);
 *  - the Omni-Torn server, with a sign-in token. The key goes there at sign-in, to prove who you
 *    are — and, once this browser has shown that it's kept, for background tracking (kept there
 *    encrypted until tracking is switched off).
 */
import { KEY_GONE_CODES, OWN_USE_HEADER, parseKeyInfo, TERMS_VERSION, WEBSITE_MAX_PER_MINUTE, type KeyAccess, type Level, type ResolvedLevel, type Snapshot, type WebPrefs, type AccessTable, type MyAccess } from '@omni-torn/shared';
import { extension, extensionSignIn, extensionTorn } from './bridge';
import { forgetSecret, loadSecret, saveSecret } from './vault';

export interface SignIn {
  mode: 'key' | 'extension';
  /** Only when you typed it on this site. */
  key?: string;
  token: string;
  until: number;
  playerId: number;
  name: string;
  access: KeyAccess | null;
}

export interface Me {
  playerId: number;
  name: string;
  factionId: number | null;
  owner: boolean;
  level: ResolvedLevel;
  access: AccessTable;
  /** The owner's "This player" set for you (Owner → Who gets what). */
  mine?: MyAccess;
  prefs: WebPrefs;
  history: { count: number; first: number | null; last: number | null };
  /** Background tracking: whether the server has a working key for you (and what it keeps). */
  tracking: { enabled: boolean; tracked: boolean; status: 'ok' | 'paused' | 'bad' | 'none'; accessLevel: number | null; problem: string | null; since: number | null; lastOkAt: number | null; attacks: number; logs: number };
  /** Your own history is kept on the Omni-Torn server (terms 19): pages ask it first (app/own-history.ts). */
  ownHistory?: boolean;
}

export class TornError extends Error {
  constructor(
    readonly code: number,
    message: string,
  ) {
    super(message);
  }
}

export class ServerError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

let current: SignIn | null = null;
/** Goes up on every sign-out: an answer that arrives after it (a renewal in flight) is dropped. */
let generation = 0;
let remembered = false;
let tornBase = 'https://api.torn.com';
let me: Me | null = null;
const signedOutListeners: Array<(why: string) => void> = [];

export const session = {
  get(): SignIn | null {
    return current;
  },
  me(): Me | null {
    return me;
  },
  level(): Level {
    return me?.level.level ?? 'basic';
  },
  remembered(): boolean {
    return remembered;
  },
  setTornBase(origin: string | undefined): void {
    if (origin && /^https?:\/\/[^/]+$/.test(origin)) tornBase = origin;
  },
  onSignedOut(f: (why: string) => void): void {
    signedOutListeners.push(f);
  },
};

const KEY_RE = /^[A-Za-z0-9]{16}$/;

/** Torn's error codes → plain words. */
export function tornErrorText(code: number, fallback: string): string {
  const t: Record<number, string> = {
    1: 'No key was given.',
    2: 'Torn doesn’t know that key (check it, or make a new one).',
    5: 'Too many Torn requests this minute — wait a little.',
    8: 'Your IP is temporarily blocked by Torn.',
    9: 'The Torn API is switched off right now.',
    10: 'Your Torn account is in federal jail — keys don’t work.',
    13: 'The key is turned off because you haven’t been online for 7 days.',
    16: 'Your key’s access level is too low for this.',
    17: 'Torn had a hiccup — try again.',
    18: 'The key is paused (Torn → Settings → API keys).',
  };
  return t[code] ?? fallback;
}

/** Calls this site made in the last minute (Torn allows 100 a minute across all your tools). */
const recentCalls: number[] = [];

async function tornDirect(key: string, path: string, params: Record<string, string>): Promise<unknown> {
  const now = Date.now();
  while (recentCalls.length && now - recentCalls[0]! >= 60_000) recentCalls.shift();
  if (recentCalls.length >= WEBSITE_MAX_PER_MINUTE) throw new TornError(0, 'This site’s share of your Torn API calls for this minute is used up — try again in a moment.');
  recentCalls.push(now);
  const url = new URL(tornBase + path);
  for (const [k, v] of Object.entries(params)) url.searchParams.set(k, v);
  url.searchParams.set('comment', 'OmniTornSite');
  let res: Response;
  try {
    res = await fetch(url, { headers: { Authorization: `ApiKey ${key}`, accept: 'application/json' }, signal: AbortSignal.timeout(20_000) });
  } catch {
    throw new TornError(0, 'Couldn’t reach the Torn API.');
  }
  const body = (await res.json().catch(() => null)) as { error?: { code?: number; error?: string } } | null;
  if (!body) throw new TornError(0, `Torn answered ${res.status}.`);
  if (body.error) throw new TornError(Number(body.error.code ?? 0), tornErrorText(Number(body.error.code ?? 0), body.error.error ?? 'Torn refused.'));
  return body;
}

/** How many more Torn calls this site may make this minute (its share of your limit) — signed in through the extension, its own limit applies. */
export function tornCallsLeft(now = Date.now()): number {
  if (current?.mode !== 'key') return WEBSITE_MAX_PER_MINUTE;
  return Math.max(0, WEBSITE_MAX_PER_MINUTE - recentCalls.filter((t) => now - t < 60_000).length);
}

const cache = new Map<string, { at: number; p: Promise<unknown> }>();

/** One Torn API read (v1 paths like "/user/", v2 like "/v2/key/info"), kept 30 s so cards share it. */
export function torn(path: string, params: Record<string, string> = {}, maxAgeMs = 30_000): Promise<unknown> {
  const s = current;
  if (!s) return Promise.reject(new TornError(0, 'Not signed in.'));
  const id = `${path}?${new URLSearchParams(params).toString()}`;
  const hit = cache.get(id);
  if (hit && Date.now() - hit.at < maxAgeMs) return hit.p;
  const p = (async () => {
    if (s.mode === 'key' && s.key) {
      try {
        return await tornDirect(s.key, path, params);
      } catch (err) {
        // Torn's terms: a key Torn calls wrong, paused or disabled is removed — forgotten here, signed out.
        if (err instanceof TornError && KEY_GONE_CODES.includes(err.code) && current === s) signOut(`Torn stopped accepting your key (${err.message}), so it was removed from this browser. Sign in with a working key.`);
        throw err;
      }
    }
    const r = await extensionTorn(path, params, s.playerId);
    if (!r) throw new TornError(0, 'The Omni-Torn extension didn’t answer.');
    if (r.error) throw new TornError(r.error.code, tornErrorText(r.error.code, r.error.message));
    return r.body;
  })();
  cache.set(id, { at: Date.now(), p });
  p.catch(() => cache.delete(id));
  return p;
}

export function clearTornCache(): void {
  cache.clear();
}

/** Ask the Omni-Torn server (signed in). A sign-in that ran out is renewed once on the way. */
export async function server<T>(method: 'GET' | 'POST' | 'PUT' | 'DELETE', path: string, body?: unknown, retry = true): Promise<T> {
  const s = current;
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      // While this tab is on screen a person is using it (the idle backfill waits — OWN_USE_HEADER); a hidden tab's timers aren't use.
      headers: { accept: 'application/json', ...(body !== undefined ? { 'content-type': 'application/json' } : {}), ...(s ? { authorization: `Bearer ${s.token}` } : {}), ...(typeof document !== 'undefined' && document.visibilityState === 'visible' ? { [OWN_USE_HEADER]: '1' } : {}) },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch {
    throw new ServerError(0, 'Couldn’t reach the Omni-Torn server.');
  }
  const data = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
  if (res.status === 401 && s && retry) {
    if (await renew(true, s.token)) return server(method, path, body, false);
    signOut('Your sign-in ran out — sign in again.');
  }
  if (!res.ok) throw new ServerError(res.status, data?.error?.message ?? `The server answered ${res.status}.`);
  return data as T;
}

const SEEN_KEY = 'omni-torn.trackSeen';
/** This browser showed that the key is kept for background tracking (the sign-in page, Account, or the notice). */
export function trackingSeen(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) === '1';
  } catch {
    return false;
  }
}
export function markTrackingSeen(): void {
  try {
    localStorage.setItem(SEEN_KEY, '1');
  } catch {
    /* this visit only */
  }
}

async function serverSignIn(key: string, agreed = false): Promise<{ token: string; until: number; playerId: number; name: string }> {
  // "seen": this browser showed that the key is kept (encrypted) for background tracking — only then the server keeps it.
  // Agreed (the sign-in page's "Agree and use my key", terms 17): one agreement, both uses — your account and your faction.
  const res = await fetch('/api/session', { method: 'POST', headers: { 'content-type': 'application/json', accept: 'application/json' }, body: JSON.stringify({ key, ...(trackingSeen() ? { tracking: 'seen' } : {}), ...(agreed ? { terms: TERMS_VERSION, uses: 'both' } : {}) }) }).catch(() => null);
  const data = (await res?.json().catch(() => null)) as { token?: string; until?: number; playerId?: number; name?: string; error?: { message?: string } } | null;
  if (!res?.ok || !data?.token) throw new ServerError(res?.status ?? 0, data?.error?.message ?? 'The Omni-Torn server didn’t sign you in.');
  return { token: data.token, until: Number(data.until), playerId: Number(data.playerId), name: String(data.name ?? '') };
}

/** Sign in with a key typed on this site: Torn says what it can read, the server says who you are. */
export async function signInWithKey(key: string, remember: boolean): Promise<void> {
  key = key.trim();
  // The sign-in page shows how the key is kept (the disclosure table) before this.
  markTrackingSeen();
  if (!KEY_RE.test(key)) throw new TornError(0, 'A Torn API key is 16 letters and digits.');
  const access = parseKeyInfo(await tornDirect(key, '/v2/key/info', {}));
  if (!access) throw new TornError(0, 'Torn didn’t say what this key can read.');
  // …and the current terms table: signing in here is agreeing to it (terms version 2: faction sharing).
  const got = await serverSignIn(key, true);
  await keep({ mode: 'key', key, token: got.token, until: got.until, playerId: got.playerId, name: got.name, access: { ...access, playerId: access.playerId ?? got.playerId } }, remember);
}

/** Sign in with a code made elsewhere (Discord /account connect, the extension, another browser): the server hands over the key it keeps. */
export async function signInWithCode(code: string, remember: boolean): Promise<void> {
  const res = await fetch('/api/session/connect', { method: 'POST', headers: { 'content-type': 'application/json', accept: 'application/json' }, body: JSON.stringify({ code }) }).catch(() => null);
  const data = (await res?.json().catch(() => null)) as { key?: string; token?: string; until?: number; playerId?: number; name?: string; error?: { message?: string } } | null;
  if (!res?.ok || !data?.key || !data.token) throw new ServerError(res?.status ?? 0, data?.error?.message ?? 'The Omni-Torn server didn’t take that code.');
  // The key was given for "your account" where the terms were shown (Discord, the extension or the site).
  markTrackingSeen();
  const access = parseKeyInfo(await tornDirect(data.key, '/v2/key/info', {}));
  if (!access) throw new TornError(0, 'Torn didn’t say what this key can read.');
  await keep({ mode: 'key', key: data.key, token: data.token, until: Number(data.until), playerId: Number(data.playerId), name: String(data.name ?? ''), access: { ...access, playerId: access.playerId ?? Number(data.playerId) } }, remember);
}

/**
 * Sign in through the extension (it keeps the key; this site gets a sign-in and asks it for Torn
 * reads). `refused`: a sign-in the server just turned down — the extension then gets a new one.
 */
export async function signInWithExtension(remember: boolean, refused?: string, fresh = false): Promise<void> {
  const gen = generation;
  const r = await extensionSignIn(refused, fresh);
  if ('problem' in r) throw new ServerError(0, r.problem);
  await keep({ mode: 'extension', token: r.token, until: r.until, playerId: r.playerId, name: r.name, access: r.key }, remember, gen);
}

async function keep(s: SignIn, remember: boolean, gen = generation): Promise<void> {
  if (gen !== generation) return; // signed out meanwhile: don't bring it back
  current = s;
  remembered = remember;
  clearTornCache();
  await saveSecret(s, remember);
}

/** Back from the browser's store: still valid? (renewed if it's running out). */
export async function restore(): Promise<boolean> {
  const got = await loadSecret<SignIn>();
  if (!got?.value?.token) return false;
  current = got.value;
  remembered = got.remembered;
  if (current.mode === 'extension') {
    const ext = await extension();
    // The extension signed out or in as someone else: this sign-in goes too. (No answer at all — it
    // may still be starting — keeps it; its Torn reads say so if it stays away.)
    if (ext && (!ext.signedIn || ext.playerId !== current.playerId)) {
      signOut(ext.signedIn ? 'The extension is signed in as someone else now — sign in again.' : 'The extension signed out — sign in again.');
      return false;
    }
  }
  if (current.until - Date.now() < 6 * 3_600_000 && !(await renew(false))) {
    if (current.until < Date.now()) {
      signOut('Your sign-in ran out — sign in again.');
      return false;
    }
  }
  return true;
}

/** For lookups (api.ts): the server said the sign-in ran out — renew it once. */
export function renewNow(): Promise<boolean> {
  return renew(true, current?.token);
}

/** Renew the server sign-in: with the token alone; else with the key you gave here; else via the extension. */
async function renew(force: boolean, refused?: string): Promise<boolean> {
  const s = current;
  if (!s) return false;
  const gen = generation;
  if (!force || s.until > Date.now()) {
    const res = await fetch('/api/session/refresh', { method: 'POST', headers: { authorization: `Bearer ${s.token}`, accept: 'application/json' } }).catch(() => null);
    const data = (await res?.json().catch(() => null)) as { token?: string; until?: number } | null;
    if (res?.ok && data?.token) {
      await keep({ ...s, token: data.token, until: Number(data.until) }, remembered, gen);
      return gen === generation;
    }
  }
  try {
    if (s.mode === 'key' && s.key) {
      const got = await serverSignIn(s.key);
      await keep({ ...s, token: got.token, until: got.until }, remembered, gen);
      return gen === generation;
    }
    if (s.mode === 'extension') {
      await signInWithExtension(remembered, refused ?? s.token);
      return gen === generation && current?.token !== refused;
    }
  } catch {
    /* falls through */
  }
  return false;
}

export async function loadMe(): Promise<Me> {
  me = await server<Me>('GET', '/api/me');
  return me;
}

let handedOver = false;

/**
 * Background tracking is on but the server has no working key for you (signed in before it kept
 * keys, or the kept one stopped working): sign in again once, so it gets the key — the one typed
 * here, or the extension's (it signs in afresh).
 */
export async function handOverKey(force = false): Promise<boolean> {
  const s = current;
  const m = me;
  if (!s || !m || !m.tracking.enabled || !m.prefs.track || (m.tracking.tracked && m.tracking.status === 'ok' && !force) || (handedOver && !force) || !trackingSeen()) return false;
  handedOver = true;
  const gen = generation;
  try {
    if (s.mode === 'key' && s.key) {
      const got = await serverSignIn(s.key);
      await keep({ ...s, token: got.token, until: got.until }, remembered, gen);
    } else await signInWithExtension(remembered, undefined, true);
    return true;
  } catch {
    return false;
  }
}

export function setPrefsLocally(p: WebPrefs): void {
  if (me) me = { ...me, prefs: p };
}

/** Forget the sign-in on this browser (the extension, if any, stays signed in). */
export function signOut(why = ''): void {
  generation++;
  const was = !!current;
  current = null;
  me = null;
  clearTornCache();
  forgetSecret();
  if (was || why) for (const f of signedOutListeners) f(why);
}

/** A key with more access: check it, and if it's yours, use it from now on. */
export async function upgradeKey(key: string): Promise<KeyAccess> {
  key = key.trim();
  if (!KEY_RE.test(key)) throw new TornError(0, 'A Torn API key is 16 letters and digits.');
  const access = parseKeyInfo(await tornDirect(key, '/v2/key/info', {}));
  if (!access) throw new TornError(0, 'Torn didn’t say what this key can read.');
  const gen = generation;
  // Account → "Use a different key" shows Torn's terms table right above the form: giving the key there agrees to it.
  markTrackingSeen();
  const got = await serverSignIn(key, true);
  if (current && got.playerId !== current.playerId) throw new TornError(0, 'That key belongs to someone else — sign out first to switch players.');
  await keep({ mode: 'key', key, token: got.token, until: got.until, playerId: got.playerId, name: got.name, access: { ...access, playerId: got.playerId } }, remembered, gen);
  return access;
}

/** Re-read what the key can do (after you changed it on Torn). */
export async function recheckKey(): Promise<void> {
  const s = current;
  if (!s) return;
  if (s.mode === 'key' && s.key) {
    const access = parseKeyInfo(await tornDirect(s.key, '/v2/key/info', {}));
    if (access) await keep({ ...s, access: { ...access, playerId: s.playerId } }, remembered);
  } else {
    await signInWithExtension(remembered);
  }
}

// History kept by the website itself (when you said yes), at most one an hour from this tab.
export async function history(days: number, fields?: 'stats'): Promise<Snapshot[]> {
  return (await server<{ history: Snapshot[] }>('GET', `/api/me/history?days=${days}${fields ? `&fields=${fields}` : ''}`)).history;
}
